import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { INominatingOrganisation, IRepresentative } from '../shared/models/nom-org';
import { Subscription } from 'rxjs';
import { materialSymbols } from '../../../shared/constants/material-symbols';
import { IOption } from '../../controls/models/option';
import { ITableColumn, TableColumnType } from '../../controls/table/table-column';
import { ITableFilter } from '../../controls/table/table-filter';
import { TableComponent } from '../../controls/table/table.component';


@Component({
    selector: 'app-nom-org-representative',
    templateUrl: './nom-org-representative.component.html',
    styleUrl: './nom-org-representative.component.scss',
    standalone: false
})

export class NomOrgRepresentativeComponent implements OnInit, OnDestroy, OnChanges {

  @ViewChild(TableComponent) tableComponent!: TableComponent<IRepresentative>;

  @Input() loading: boolean = false;
  @Input() nominatingOrganisation: INominatingOrganisation | null = null;
  representatives: IRepresentative[] = [];
  searchIcon: string = materialSymbols.search;
  close: string = materialSymbols.close;
  committeeOptions: IOption<string>[] = [];
  roleOptions: IOption<string>[] = [];
  subscriptions: Subscription = new Subscription();

  tableId = 'table-nom-org-representatives';
  tableData: IRepresentative[] = [];
  tableColumns: ITableColumn<IRepresentative>[] = [
    {
      name: 'memberFullName',
      displayName: 'Name',
      isSortable: true,
    },
    {
      name: 'memberEmail',
      displayName: 'Email',
      type: TableColumnType.link,
      isSortable: true,
      getLink: (row: IRepresentative) => `mailto:${row.memberEmail}`,
    },
    {
      name: 'nationalCommitteeCode',
      displayName: 'Represented On',
      type: TableColumnType.routerLink,
      isSortable: true,
      getLink: (row: IRepresentative) => `/committees/${row.nationalCommitteeCode}`,
      headerWidth: '220px'
    },
    {
      name: 'membershipType',
      displayName: 'Role',
      isSortable: true,
    },
  ];
  tableFilters: ITableFilter[] = [];

  formGroupRepresentatives : FormGroup = new FormGroup({
    searchTerm: new FormControl<string>(''),
    selectedCommittees: new FormControl<string[]>([]),
    selectedRoles: new FormControl<string[]>([]),
  });

  ngOnInit(): void {
    this.populateFilters();

    this.subscriptions.add(this.formGroupRepresentatives.valueChanges.subscribe((formGroupData: Partial<IFormGroupRepresentativesData>) => {
      this.onFilterChange(formGroupData);
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes['nominatingOrganisation'] && changes['nominatingOrganisation'].firstChange === false) {
      this.nominatingOrganisation = changes['nominatingOrganisation'].currentValue;
      this.representatives = this.nominatingOrganisation?.representatives || [];
      this.tableData = this.representatives;
      this.resetFilters();
      this.populateFilters();
    }
  }


  populateFilters(): void {
    const roles = Array.from(new Set(this.representatives.map(rep => rep.membershipType)))
      .sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));

    this.roleOptions = roles.map(role => ({
      value: role.toString(),
      label: role.toString()
    }));

    const committees = Array.from(new Set(this.representatives.map(rep => rep.nationalCommitteeCode)))
      .sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()));

    this.committeeOptions = committees.map(committee => ({
      value: committee,
      label: committee
    }));
  }

  resetFilters(): void {
    let emptyFilter: IFormGroupRepresentativesData = {
      searchTerm: '',
      selectedCommittees: [],
      selectedRoles: []
    };

    this.formGroupRepresentatives.patchValue(emptyFilter);
  }

  onFilterChange(formGroupData: Partial<IFormGroupRepresentativesData>): void {
      if (!formGroupData) {
      return;
    }

    const filters: ITableFilter[] = [];

    if (formGroupData.searchTerm && formGroupData.searchTerm.length > 0) {
      filters.push({
        properties: ['memberFullName'],
        value: formGroupData.searchTerm
      });
    }

    if (formGroupData.selectedCommittees && formGroupData.selectedCommittees.length > 0) {
      filters.push({
        properties: ['nationalCommitteeCode'],
        value: formGroupData.selectedCommittees
      });
    }

    if (formGroupData.selectedRoles && formGroupData.selectedRoles.length > 0) {
      filters.push({
        properties: ['membershipType'],
        value: formGroupData.selectedRoles
      });
    }

    this.tableFilters = filters;
  }
}

interface IFormGroupRepresentativesData {
  searchTerm: string;
  selectedCommittees: string[];
  selectedRoles: string[];
}


